import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import DonationLanding from "./misc/DonationLanding";
import db from "../../constants/database";
import { FaChevronDown } from "react-icons/fa";
import FooterComponent from "../../themes/footer";
import Navbar from "../../themes/header/nav_bar";
import DonationModal from "./misc/DonateModal";
import { useAppDispatch } from "../../store/hooks";
import {
  create_donation,
  // capture_donation,
  CreateDonationData,
} from "../../services/api/donation";

// --- Types ---------------------------------------------------------------
interface Slide {
  id: string;
  image: any;
  alt: string;
  headline: string;
  sub: string;
  cta: string;
}

interface Cause {
  id: string;
  name: string;
  image: any;
  image2?: any;
  summary: string;
  subtitle?: string;
  bullets: string[];
  donateUrl?: any | null;
  buttonText?: string;
  fundType?: string;
}

// interface DonationFormData {
//   foundation: string;
//   amount: number | null;
//   name: string;
//   email: string;
// }

// --- Helpers -------------------------------------------------------------
function classNames(...cls: (string | false | null | undefined)[]) {
  return cls.filter(Boolean).join(" ");
}

// --- Modal (custom, accessible) -----------------------------------------
function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const firstFocusable = useRef<HTMLButtonElement | null>(null);
  const lastFocusable = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const prev = document.activeElement as HTMLElement | null;

    // lock scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // focus first element
    firstFocusable.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") {
        // simple focus trap
        const active = document.activeElement;
        if (e.shiftKey && active === firstFocusable.current) {
          e.preventDefault();
          lastFocusable.current?.focus();
        } else if (!e.shiftKey && active === lastFocusable.current) {
          e.preventDefault();
          firstFocusable.current?.focus();
        }
      }
    };

    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prev?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      aria-modal="true"
      role="dialog"
      aria-labelledby="modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-2xl border-b px-6 py-4">
          <h2 id="modal-title" className="text-lg font-semibold text-gray-900">
            {title}
          </h2>
          <button
            ref={firstFocusable}
            onClick={onClose}
            aria-label="Close dialog"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
        <div className="px-6 py-3 text-sm leading-6 text-gray-700">
          {children}
        </div>
        {/* <div className="flex items-center justify-end gap-3 rounded-b-2xl border-t px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-xl border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
          <button ref={lastFocusable} className="sr-only" aria-hidden="true">
            hidden
          </button>
        </div> */}
      </div>
    </div>
  );
}

// --- Hero Slider (custom) -----------------------------------------------
function HeroSlider({ slides }: { slides: Slide[] }) {
  const [index, setIndex] = useState(0);
  const [pause, setPause] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const next = useCallback(
    () => setIndex((i) => (i + 1) % slides.length),
    [slides.length],
  );
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + slides.length) % slides.length),
    [slides.length],
  );

  useEffect(() => {
    if (pause) return;
    timeoutRef.current && window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(next, 5000);
    return () => {
      timeoutRef.current && window.clearTimeout(timeoutRef.current);
    };
  }, [index, pause, slides.length, next]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [next, prev]);

  // const [isModalOpen, setIsModalOpen] = useState(false);

  // const handleDonationSubmit = (data: DonationFormData) => {
  //   console.log("Donation submitted:", data);
  //   alert(
  //     `Thank you for your donation of $${data.amount} to ${data.foundation}!`
  //   );
  //   setIsModalOpen(false);
  // };

  return (
    <section
      className="relative h-[480px] w-full overflow-hidden font-abel"
      aria-roledescription="carousel"
      aria-label="Give Back hero"
      onMouseEnter={() => setPause(true)}
      onMouseLeave={() => setPause(false)}
    >
      {/* Slides */}
      <div
        className="absolute inset-0 flex h-full w-full transition-transform duration-700"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {slides.map((s) => (
          <div
            key={s.id}
            className="relative h-full w-full flex-none text-[white] text-center"
          >
            <img
              src={s.image}
              alt={s.alt}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[black]/60 via-[black]/30 to-transparent" />
            <div className="relative z-10 mx-auto flex justify-center h-full max-w-6xl items-center px-6">
              <div className="max-w-2xl">
                <p className="text-3xl tracking-tight text-white md:text-[50px] pb-1">
                  {s.headline}
                </p>
                <p className="mt-3 text-white text-lg md:text-[22px] max-w-[600px] mx-auto">
                  {s.sub}
                </p>
                <div className="mt-7 flex justify-center gap-3">
                  <a
                    href="#give"
                    className="rounded-[10px] bg-[#036CFF] px-4 py-2 text-lg text-white shadow hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-white/60"
                  >
                    {s.cta}
                  </a>
                  {/* <button
                    aria-label="Previous slide"
                    onClick={prev}
                    className="inline-flex items-center rounded-xl bg-white/90 px-3 py-2 text-sm font-medium text-gray-900 backdrop-blur hover:bg-white"
                  >
                    ←
                  </button>
                  <button
                    aria-label="Next slide"
                    onClick={next}
                    className="inline-flex items-center rounded-xl bg-white/90 px-3 py-2 text-sm font-medium text-gray-900 backdrop-blur hover:bg-white"
                  >
                    →
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dots */}
      <div className="pointer-events-none absolute bottom-4 left-1/2 z-10 -translate-x-1/2">
        <div className="flex items-center gap-2">
          {slides.map((s, i) => (
            <button
              key={s.id}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setIndex(i)}
              className={classNames(
                "pointer-events-auto h-2.5 w-2.5 rounded-full border border-white/70",
                i === index ? "bg-white" : "bg-white/20 hover:bg-white/40",
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// --- Cause Card ---------------------------------------------------------
function CauseCard({
  cause,
  onLearn,
  onDonate,
}: {
  cause: Cause;
  onLearn: (c: Cause) => void;
  onDonate: () => void;
}) {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-[8.55px] border bg-[white] shadow-sm font-abel">
      <div className="relative">
        <img
          src={cause.image}
          alt="25th and Staffing"
          className="h-44 w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
      </div>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <h3 className="text-base font-semibold text-gray-900">{cause.name}</h3>
        <p className="text-sm text-gray-600">{cause.summary}</p>
        <div>
          <ul className="space-y-1 text-sm text-gray-700">
            {cause.bullets.map((b, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="mt-2 inline-block h-1.5 w-1.5 flex-none rounded-full bg-[black]" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-auto pt-2">
          <div className="mb-3">
            <button
              onClick={() => onLearn(cause)}
              className="rounded-2xl flex items-center px-3 py-1.5 text-sm bg-[#0B60D6]/20 text-[#243677] hover:bg-blue-50"
            >
              Learn More{" "}
              <span className="ml-1">
                <FaChevronDown />
              </span>
            </button>
          </div>
          <button
            onClick={onDonate}
            className="rounded-full bg-[#243677] w-full flex justify-center px-4 py-2 text-sm md:text-lg text-[white] hover:text-[white] hover:bg-[#036CFF]"
          >
            {cause.buttonText}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Page ---------------------------------------------------------------
export default function GiveWithPurpose() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");
  const orderID = searchParams.get("orderID"); // Check both common params

  useEffect(() => {
    if (orderID || token) {
      // Capture donation
      // capture_donation(dispatch, {
      //   orderID: (orderID || token) as string,
      // }).then(() => {
      //   // Remove params from URL to clean up
      //   window.history.replaceState(
      //     {},
      //     document.title,
      //     window.location.pathname
      //   );
      // });
      // Redirect to status page for capture
      const params = new URLSearchParams();
      if (orderID) params.append("orderID", orderID);
      if (token) params.append("token", token);

      // Use window.location or navigate? window.location might be safer to ensure clean state or just use navigate if possible but I don't have navigate hook here yet.
      // Wait, I can use window.location.href or just history.push logic if I import useNavigate.
      // I only imported useLocation.
      // Let's assume I can use window.location.href or I should add useNavigate.
      // Since I'm in a functional component inside Router (AnimatedRoute), I can use useNavigate.
      // Just need to import it.

      // But for now, window.location.assign works too.
      window.location.assign(`/give/status?${params.toString()}`);
    }
  }, [orderID, token]);

  const slides: Slide[] = useMemo(
    () => [
      {
        id: "s1",
        image: db.giveSlider,
        alt: "25th",
        headline: "Give Back. Make a Real Impact Today",
        sub: "At 25th and Staffing, giving back is part of our DNA. We believe in using our success to empower others with integrity, transparency, and heart.",
        cta: "Give with Purpose",
      },
      // {
      //   id: "s2",
      //   image: db.giveSlider2,
      //   alt: "give",
      //   headline: "Every Gift Moves Someone Forward",
      //   sub: "At 25th and Staffing, giving back is part of our DNA. We believe in using our success to empower others with integrity, transparency, and heart.",
      //   cta: "Give with Purpose",
      // },
      {
        id: "s2",
        image: db.giveSlider3,
        alt: "give",
        headline: "Small Acts. Big Change.",
        sub: "At 25th and Staffing, giving back is part of our DNA. We believe in using our success to empower others with integrity, transparency, and heart.",
        cta: "Give with Purpose",
      },
      {
        id: "s4",
        image: db.giveSlider4,
        alt: "give",
        headline: "Every Gift Moves Someone Forward",
        sub: "At 25th and Staffing, giving back is part of our DNA. We believe in using our success to empower others with integrity, transparency, and heart.",
        cta: "Give with Purpose",
      },
    ],
    [],
  );

  const causes: Cause[] = [
    {
      id: "impact",
      name: "General Impact Fund",
      image: db.world,
      subtitle:
        "The General Impact Fund ensures resources reach the areas of greatest need, from urgent community support to sustainable programs. Your contribution has broad, measurable impact.",
      image2: db.generalImpact,
      summary: "Focused on:",
      bullets: [
        "Motherless babies & orphans support",
        "Widows empowerment programs",
        "Education & skills development",
        "Community empowerment projects",
        "Your donation is used where help is needed most",
      ],
      donateUrl: true,
      buttonText: "Donate Now",
      fundType: "GENERAL_IMPACT",
    },
    {
      id: "kinabuti",
      name: "Kinabuti Initiative",
      image: db.kinabuti,
      subtitle:
        "The Kinabuti Initiative empowers youth to reach their full potential through education, mentorship, and community programs. Donations directly support learning materials, mentorship sessions, and sustainable community projects.",
      image2: db.kinabuti2,
      summary: "Focused on:",
      bullets: [
        "Providing education, mentorship, and life skills programs for underprivileged youth",
        "Supporting community development projects that uplift vulnerable families",
        "Support a targeted cause that aligns with your passion",
      ],
      donateUrl: true,
      buttonText: "Sponsor a Child",
      fundType: "KINABUTI",
    },
    // {
    //   id: "orphanage",
    //   name: "Living Foundation Orphanage",
    //   image:
    //     "https://images.unsplash.com/photo-1542810634-71277d95dcbb?q=80&w=1500&auto=format&fit=crop",
    //   summary: "",
    //   bullets: [
    //     "Shelter, nutrition & healthcare",
    //     "Education and emotional support to vulnerable children",
    //     "Creating pathways to stable, nurturing futures",
    //   ],
    //   donateUrl: "#",
    // },
  ];

  const [open, setOpen] = useState(false);
  const [activeCause, setActiveCause] = useState<Cause | null>(null);

  const onLearn = (c: Cause) => {
    setActiveCause(c);
    setOpen(true);
  };

  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  const [selectedFoundation, setSelectedFoundation] =
    useState("GENERAL_IMPACT");

  const handleOpenDonation = (foundation: string = "GENERAL_IMPACT") => {
    setSelectedFoundation(foundation);
    setIsDonationModalOpen(true);
  };

  const handleDonationSubmit = (data: any) => {
    // console.log("Donation submitted:", data);
    // alert(
    //   `Thank you for your donation of $${data.amount} to ${data.foundation}!`
    // );
    // setIsModalOpen(false);

    // Convert modal data to API format if needed
    // The Modal returns foundation string like "GENERAL_IMPACT" or "KINABUTI" due to our changes
    // But we need to ensure it matches API.

    const payload: CreateDonationData = {
      fundType: data.foundation, // Ensure this matches what backend expects
      amount: data.amount,
      name: data.name,
      email: data.email,
    };

    create_donation(dispatch, payload);
    setIsDonationModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-abel">
      <Navbar />

      <HeroSlider slides={slides} />

      {/* Give with Purpose */}
      <main id="give" className="bg-[#E7E4E4]  px-6 pb-24 pt-14">
        <div className="mx-auto max-w-[1122px]">
          <header className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl md:text-[50px] tracking-tight text-[black] md:text-3xl">
              Give with Purpose
            </h2>
            <p className="mt-3 text-lg md:text-[21px] text-[#504D4D]">
              Choose a cause that speaks to you, and your contribution will go
              directly where it's needed most. Every gift makes a real
              difference.
            </p>
          </header>

          <section className="mt-10 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-2 max-w-[740px] mx-auto">
            <div className="md:col-span-2 col-span-1">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-2">
                {causes.map((cause) => (
                  <CauseCard
                    key={cause.id}
                    cause={cause}
                    onLearn={onLearn}
                    onDonate={() =>
                      handleOpenDonation(cause.fundType || "GENERAL_IMPACT")
                    }
                  />
                ))}
              </div>
            </div>
            {/* <div className="col-span-1 bg-soonBg bg-cover bg-center bg-no-repeat rounded-[8.55px]"></div> */}
          </section>
        </div>
      </main>

      <DonationLanding onDonate={handleOpenDonation} />
      <FooterComponent />

      {/* Modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={activeCause?.name || "Cause"}
      >
        <div className="space-y-4">
          {activeCause && (
            <>
              <p className="text-gray-700 text-sm md:text-[17px]">
                {activeCause.subtitle}
              </p>
              <img src={activeCause.image2} alt="give" className="w-full" />
              {/* <ul className="list-disc space-y-1 pl-5">
                {activeCause.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul> */}
              <div className="pt-1 text-center">
                <button
                  onClick={() => {
                    setOpen(false);
                    handleOpenDonation(
                      activeCause.fundType || "GENERAL_IMPACT",
                    );
                  }}
                  // href={activeCause.donateUrl || "#"}
                  className="inline-flex items-center rounded-full bg-[#243677] px-5 py-2 text-lg font-semibold text-white hover:bg-blue-500 cursor-pointer"
                >
                  Donate Now
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
      <DonationModal
        isOpen={isDonationModalOpen}
        onClose={() => setIsDonationModalOpen(false)}
        onSubmit={handleDonationSubmit}
        initialFoundation={selectedFoundation}
      />
    </div>
  );
}
